/**
 * FAQ Hub – Live-Suche mit smarten Vorschlägen.
 *
 * Arbeitet rein clientseitig auf dem bereits gerenderten DOM:
 * - Fuzzy/Teilwort-Matching über Frage, Antwort und Keywords
 * - Vorschlagsliste (Autocomplete) während des Tippens
 * - Blendet nicht passende Items und leere Kategorien aus
 * - Öffnet Treffer automatisch und hebt den Suchbegriff hervor
 * - Deep-Linking über ?q= und #hash
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('FaqHub', () => ({
    query: '',
    suggestions: [],
    activeSuggestion: -1,
    showSuggestions: false,
    resultCount: 0,
    hasQuery: false,
    items: [],
    categories: [],
    debounceTimer: null,

    init() {
      this.collect();

      const params = new URLSearchParams(window.location.search);
      const initial = params.get('q');
      if (initial) {
        this.query = initial;
        // Warten bis die verschachtelten Alpine-Komponenten der Items bereit sind
        this.$nextTick(() => this.applyFilter());
      }
    },

    collect() {
      this.items = Array.from(this.$root.querySelectorAll('[data-faq-item]')).map(
        (el) => ({
          el,
          question: el.dataset.faqQuestion || '',
          haystack: this.normalize(el.dataset.faqHaystack || ''),
        })
      );

      this.categories = Array.from(
        this.$root.querySelectorAll('[data-faq-category]')
      );
    },

    /** Diakritika entfernen + Umlaute normalisieren, damit "gruss" auch "Gruß" findet. */
    normalize(value) {
      return value
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    },

    get tokens() {
      return this.normalize(this.query.trim())
        .split(/\s+/)
        .filter((token) => token.length > 1);
    },

    matches(item, tokens) {
      return tokens.every((token) => item.haystack.includes(token));
    },

    onInput() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.buildSuggestions();
        this.applyFilter();
      }, 150);
    },

    buildSuggestions() {
      const tokens = this.tokens;

      if (!tokens.length) {
        this.suggestions = [];
        this.showSuggestions = false;
        this.activeSuggestion = -1;
        return;
      }

      const matched = this.items.filter((item) => this.matches(item, tokens));

      // Treffer, bei denen der Begriff in der Frage steht, zuerst
      matched.sort((a, b) => {
        const aInQuestion = this.normalize(a.question).includes(tokens[0]);
        const bInQuestion = this.normalize(b.question).includes(tokens[0]);
        if (aInQuestion === bInQuestion) return 0;
        return aInQuestion ? -1 : 1;
      });

      this.suggestions = matched.slice(0, 6).map((item) => ({
        question: item.question,
        el: item.el,
      }));

      this.showSuggestions = this.suggestions.length > 0;
      this.activeSuggestion = -1;
    },

    onKeydown(event) {
      if (!this.showSuggestions) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.activeSuggestion =
            (this.activeSuggestion + 1) % this.suggestions.length;
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.activeSuggestion =
            this.activeSuggestion <= 0
              ? this.suggestions.length - 1
              : this.activeSuggestion - 1;
          break;
        case 'Enter':
          if (this.activeSuggestion > -1) {
            event.preventDefault();
            this.selectSuggestion(this.activeSuggestion);
          }
          break;
        case 'Escape':
          this.showSuggestions = false;
          this.activeSuggestion = -1;
          break;
      }
    },

    selectSuggestion(index) {
      const suggestion = this.suggestions[index];
      if (!suggestion) return;

      this.query = suggestion.question;
      this.showSuggestions = false;
      this.activeSuggestion = -1;
      this.applyFilter();

      this.$nextTick(() => {
        this.openItem(suggestion.el);
        suggestion.el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },

    openItem(el) {
      const data = Alpine.$data(el);
      if (data && 'expanded' in data) data.expanded = true;
    },

    closeItem(el) {
      const data = Alpine.$data(el);
      if (data && 'expanded' in data) data.expanded = false;
    },

    applyFilter() {
      const tokens = this.tokens;
      this.hasQuery = tokens.length > 0;

      if (!this.hasQuery) {
        this.items.forEach((item) => {
          item.el.hidden = false;
          this.closeItem(item.el);
        });
        this.categories.forEach((category) => {
          category.hidden = false;
        });
        this.resultCount = 0;
        this.updateUrl('');
        return;
      }

      let count = 0;

      this.items.forEach((item) => {
        const hit = this.matches(item, tokens);
        item.el.hidden = !hit;
        if (hit) {
          count += 1;
          this.openItem(item.el);
        } else {
          this.closeItem(item.el);
        }
      });

      // Kategorien ohne sichtbare Treffer ausblenden
      this.categories.forEach((category) => {
        const visible = category.querySelector('[data-faq-item]:not([hidden])');
        category.hidden = !visible;
      });

      this.resultCount = count;
      this.updateUrl(this.query.trim());
    },

    clear() {
      this.query = '';
      this.suggestions = [];
      this.showSuggestions = false;
      this.activeSuggestion = -1;
      this.applyFilter();
    },

    updateUrl(value) {
      const url = value
        ? `${window.location.pathname}?q=${encodeURIComponent(value)}`
        : window.location.pathname;
      window.history.replaceState({}, '', url);
    },

    /** Markiert den Suchbegriff im Vorschlagstext (XSS-sicher via textContent-Segmenten). */
    highlightNodes(text) {
      const tokens = this.tokens;
      if (!tokens.length) return [{ text, match: false }];

      const normalized = this.normalize(text);
      const token = tokens[0];
      const start = normalized.indexOf(token);

      if (start === -1) return [{ text, match: false }];

      return [
        { text: text.slice(0, start), match: false },
        { text: text.slice(start, start + token.length), match: true },
        { text: text.slice(start + token.length), match: false },
      ].filter((part) => part.text.length > 0);
    },
  }));
});
